import { useParams } from "react-router-dom";
import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useGetCategoriesLinkedToProduct } from "../../../../hooks/api/queries";
import { useSellModeFilters } from "../../../../hooks/useSellModeFilters";
import { truncateString } from "../../../../helpers";
import styles from "./styles.module.scss";
import { CategoryExclamation } from "../../../../assets/svgIcons";
import secondaryComponents from "../..";
import { AllCategoriesView } from "../AllCategoriesView";

export const SellModeCategory = () => {
  const { store_id } = useParams();
  const { data: categories, isLoading } = useGetCategoriesLinkedToProduct({
    queries: `?store_id=${store_id}`,
  });

  const { search, filterByCategory } = useSellModeFilters();
  const containerRef = useRef(null);
  const [visibleCategories, setVisibleCategories] = useState([]);
  const [showMore, setShowMore] = useState(false);
  const [isArrangeDone, setIsArrangeDone] = useState(false);

  // NEW: track selected category with subcategories
  const [selectedCategoryWithSubs, setSelectedCategoryWithSubs] =
    useState(null);

  // Handle category filtering
  const handleCategoryClick = useCallback(
    (category) => {
      if (!category) {
        filterByCategory({ id: null, include_sub_category_products: false });
        return;
      }

      if (category?.sub_categories?.length > 0) {
        setSelectedCategoryWithSubs(category);
      } else {
        filterByCategory({
          id: category?.id,
          include_sub_category_products: false,
        });
      }
    },
    [filterByCategory],
  );

  useEffect(() => {
    if (!categories || categories?.length === 0) return;
    const container = containerRef.current;
    if (!container) return;

    const categoryElements = Array.from(container.children);
    if (categoryElements?.length === 0) return;

    let totalWidth = 40;
    const containerWidth = container.clientWidth;
    let fitCategories = [];

    categoryElements.forEach((el, i) => {
      const elWidth = el.offsetWidth + 12;
      if (totalWidth + elWidth + 30 > containerWidth - 250) return;
      totalWidth += elWidth;
      fitCategories.push(`${categories[i]?.id}`);
    });

    // Ensure selected category is visible
    const selectedCategory = search.get("category_id");
    if (selectedCategory && !fitCategories.includes(selectedCategory)) {
      fitCategories[fitCategories.length - 1] = selectedCategory;
    }

    setIsArrangeDone(true);
    setVisibleCategories(fitCategories);
  }, [categories, search]);

  useEffect(() => {
    const selectedCategory = search.get("category_id");
    if (!selectedCategory) return;

    setVisibleCategories((prev) => {
      if (prev.includes(selectedCategory)) return prev;
      return [...prev.slice(0, -1), selectedCategory];
    });
  }, [search]);

  const filteredCategories = useMemo(
    () =>
      categories?.filter(
        (category) =>
          visibleCategories.includes(`${category?.id}`) || !isArrangeDone,
      ) || [],
    [categories, visibleCategories, isArrangeDone],
  );

  return (
    <>
      <section
        ref={containerRef}
        aria-label="Category selection"
        className={styles.categories}
        aria-live="polite"
      >
        <secondaryComponents.LoaderHelper
          isLoading={isLoading}
          classNames="py-3 mx-auto"
        >
          <button
            type="button"
            className={`${styles.category} ${
              !search?.get("category_id") ? styles.active : ""
            } ${styles.all}`}
            onClick={() => handleCategoryClick(null)}
            aria-pressed={!search?.get("category_id")}
          >
            All
          </button>

          {filteredCategories?.map((category) => {
            const isParentActive =
              search?.get("category_id") === String(category?.id);

            return (
              <button
                key={category?.id}
                type="button"
                className={`${styles.category} ${isParentActive ? styles.active : ""}`}
                onClick={() => handleCategoryClick(category)}
                aria-pressed={isParentActive}
              >
                {truncateString(category?.name, 15)}
              </button>
            );
          })}

          <button
            type="button"
            className={`${styles.category} ${styles.more}`}
            onClick={() => setShowMore(true)}
          >
            <span>View More</span>
            <CategoryExclamation />
          </button>
        </secondaryComponents.LoaderHelper>
      </section>

      {/* Existing "view more" modal */}
      <secondaryComponents.Modal
        isActive={showMore}
        closeModal={() => setShowMore(false)}
        width={1300}
        padding="15px 0 50px 0"
        aria-modal="true"
        role="dialog"
      >
        <AllCategoriesView closeModal={() => setShowMore(false)} />
      </secondaryComponents.Modal>

      {/* NEW modal for subcategories */}
      <secondaryComponents.Modal
        isActive={!!selectedCategoryWithSubs}
        closeModal={() => setSelectedCategoryWithSubs(null)}
        width={1300}
        padding="15px 0 50px 0"
        aria-modal="true"
        role="dialog"
      >
        <AllCategoriesView
          category={selectedCategoryWithSubs}
          closeModal={() => setSelectedCategoryWithSubs(null)}
          sub={true}
        />
      </secondaryComponents.Modal>
    </>
  );
};
